/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { FilterListsResponse2 } from '../models/filter-lists-response-2';
import { PaginatedApiResponseWithFilters } from '../models/paginated-api-response-with-filters';
import { ProfileResponse2 } from '../models/profile-response-2';

@Injectable({
  providedIn: 'root',
})
export class PspPlanningBoardService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getPspPlanningBoardList
   */
  static readonly GetPspPlanningBoardListPath = '/api/web/v1/psps/{psp}/planning-board';

  /**
   * PSP Planning Board — paginated list of profiles at this PSP within the user's
   * role-derived branch scope. Two modes:.
   *
   * - **Roster mode** (no axis filter set): every active profile in scope appears, even
   *   those with no offers or assignments in the [from, to] window. `leavingDate` gates
   *   out profiles that left before `from`.
   * - **Booking-view mode** (any of `customers[]` / `customerLocations[]` /
   *   `customerDepartments[]` / `mainQualifications[]` set): roster narrowed via
   *   OR-EXISTS — a profile appears if it has ≥1 in-window Offer (status OPEN /
   *   NEEDS_CONFIRMATION / NEEDS_UPDATE / CONFIRMED) OR ≥1 in-window Assignment matching
   *   the filter set. The filter narrows *who appears*; each visible profile still
   *   carries its FULL in-window `offers[]` and `assignments[]` at this PSP.
   *
   * Each profile entry includes embedded offers (with org-graph + OfferPositions for the
   * window), embedded assignments (with AssignmentDates for the window via bulk fetch),
   * window-overlap `absences[]`, profile-static `candidateAvailability[]`, the profile's
   * PSP branches, and `plannedHours` summed from non-canceled assignment-dates in the window.
   *
   * **Accessible to users with `FEATURE_ADMIN_OFFERS` on the PSP.**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPspPlanningBoardList()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPspPlanningBoardList$Response(params: {

    /**
     * PSP Id.
     */
    psp: number;

    /**
     * Window start (inclusive).
     */
    from: string;

    /**
     * Window end (inclusive). Must be on or after from; window must be at most 32 days.
     */
    to: string;

    /**
     * Page number in pagination.
     */
    page?: any;

    /**
     * Page size in pagination.
     */
    size?: any;

    /**
     * Search by profile.firstName, profile.lastName, profile.personalId. Max 100 chars.
     */
    search?: string;

    /**
     * Narrow the outer profile set to profiles with ≥1 in-window Offer or Assignment at one of the given Customers.
     *      *      Embedded offers[]/assignments[] still carry the profile&#x27;s full in-window data at this PSP (not narrowed by this filter). Max 200 ids.
     */
    'customers[]'?: any;

    /**
     * Narrow the outer profile set by Customer location ids; embedded offers[]/assignments[] are not narrowed by this filter. Max 200 ids.
     */
    'customerLocations[]'?: any;

    /**
     * Narrow the outer profile set by Customer department ids; embedded offers[]/assignments[] are not narrowed by this filter. Max 200 ids.
     */
    'customerDepartments[]'?: any;

    /**
     * Narrow the outer profile set by main Qualification ids; embedded offers[]/assignments[] are not narrowed by this filter. Max 200 ids.
     */
    'mainQualifications[]'?: any;

    /**
     * Narrow the outer profile set by Profile.branches; intersected with role scope (never widens). Max 200 ids.
     */
    'pspBranches[]'?: any;

    /**
     * Narrow the outer profile set by entry kind. Selected values OR within the filter;
     *      *      the filter ANDs with axis filters. Offers are not classified — Fixed/Flexible apply to non-canceled AssignmentDates only.
     *      *      &quot;Absence&quot; matches all 7 Absence types including &quot;free_shift&quot;; &quot;Free shift&quot; is the explicit subset. Max 4 values.
     */
    'entryTypes[]'?: Array<'fixed_shift' | 'flexible_shift' | 'absence' | 'free_shift'>;
    orderKey?: 'lastName' | 'firstName' | 'contractHours';
    orderDirection?: 'asc' | 'desc';
    context?: HttpContext
  }
): Observable<StrictHttpResponse<PaginatedApiResponseWithFilters & {
'data'?: Array<ProfileResponse2>;
'filters'?: FilterListsResponse2;
}>> {

    const rb = new RequestBuilder(this.rootUrl, PspPlanningBoardService.GetPspPlanningBoardListPath, 'get');
    if (params) {
      rb.path('psp', params.psp, {});
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('search', params.search, {});
      rb.query('customers[]', params['customers[]'], {});
      rb.query('customerLocations[]', params['customerLocations[]'], {});
      rb.query('customerDepartments[]', params['customerDepartments[]'], {});
      rb.query('mainQualifications[]', params['mainQualifications[]'], {});
      rb.query('pspBranches[]', params['pspBranches[]'], {});
      rb.query('entryTypes[]', params['entryTypes[]'], {});
      rb.query('orderKey', params.orderKey, {});
      rb.query('orderDirection', params.orderDirection, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaginatedApiResponseWithFilters & {
        'data'?: Array<ProfileResponse2>;
        'filters'?: FilterListsResponse2;
        }>;
      })
    );
  }

  /**
   * PSP Planning Board — paginated list of profiles at this PSP within the user's
   * role-derived branch scope. Two modes:.
   *
   * - **Roster mode** (no axis filter set): every active profile in scope appears, even
   *   those with no offers or assignments in the [from, to] window. `leavingDate` gates
   *   out profiles that left before `from`.
   * - **Booking-view mode** (any of `customers[]` / `customerLocations[]` /
   *   `customerDepartments[]` / `mainQualifications[]` set): roster narrowed via
   *   OR-EXISTS — a profile appears if it has ≥1 in-window Offer (status OPEN /
   *   NEEDS_CONFIRMATION / NEEDS_UPDATE / CONFIRMED) OR ≥1 in-window Assignment matching
   *   the filter set. The filter narrows *who appears*; each visible profile still
   *   carries its FULL in-window `offers[]` and `assignments[]` at this PSP.
   *
   * Each profile entry includes embedded offers (with org-graph + OfferPositions for the
   * window), embedded assignments (with AssignmentDates for the window via bulk fetch),
   * window-overlap `absences[]`, profile-static `candidateAvailability[]`, the profile's
   * PSP branches, and `plannedHours` summed from non-canceled assignment-dates in the window.
   *
   * **Accessible to users with `FEATURE_ADMIN_OFFERS` on the PSP.**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getPspPlanningBoardList$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPspPlanningBoardList(params: {

    /**
     * PSP Id.
     */
    psp: number;

    /**
     * Window start (inclusive).
     */
    from: string;

    /**
     * Window end (inclusive). Must be on or after from; window must be at most 32 days.
     */
    to: string;

    /**
     * Page number in pagination.
     */
    page?: any;

    /**
     * Page size in pagination.
     */
    size?: any;

    /**
     * Search by profile.firstName, profile.lastName, profile.personalId. Max 100 chars.
     */
    search?: string;

    /**
     * Narrow the outer profile set to profiles with ≥1 in-window Offer or Assignment at one of the given Customers.
     *      *      Embedded offers[]/assignments[] still carry the profile&#x27;s full in-window data at this PSP (not narrowed by this filter). Max 200 ids.
     */
    'customers[]'?: any;

    /**
     * Narrow the outer profile set by Customer location ids; embedded offers[]/assignments[] are not narrowed by this filter. Max 200 ids.
     */
    'customerLocations[]'?: any;

    /**
     * Narrow the outer profile set by Customer department ids; embedded offers[]/assignments[] are not narrowed by this filter. Max 200 ids.
     */
    'customerDepartments[]'?: any;

    /**
     * Narrow the outer profile set by main Qualification ids; embedded offers[]/assignments[] are not narrowed by this filter. Max 200 ids.
     */
    'mainQualifications[]'?: any;

    /**
     * Narrow the outer profile set by Profile.branches; intersected with role scope (never widens). Max 200 ids.
     */
    'pspBranches[]'?: any;

    /**
     * Narrow the outer profile set by entry kind. Selected values OR within the filter;
     *      *      the filter ANDs with axis filters. Offers are not classified — Fixed/Flexible apply to non-canceled AssignmentDates only.
     *      *      &quot;Absence&quot; matches all 7 Absence types including &quot;free_shift&quot;; &quot;Free shift&quot; is the explicit subset. Max 4 values.
     */
    'entryTypes[]'?: Array<'fixed_shift' | 'flexible_shift' | 'absence' | 'free_shift'>;
    orderKey?: 'lastName' | 'firstName' | 'contractHours';
    orderDirection?: 'asc' | 'desc';
    context?: HttpContext
  }
): Observable<PaginatedApiResponseWithFilters & {
'data'?: Array<ProfileResponse2>;
'filters'?: FilterListsResponse2;
}> {

    return this.getPspPlanningBoardList$Response(params).pipe(
      map((r: StrictHttpResponse<PaginatedApiResponseWithFilters & {
'data'?: Array<ProfileResponse2>;
'filters'?: FilterListsResponse2;
}>) => r.body as PaginatedApiResponseWithFilters & {
'data'?: Array<ProfileResponse2>;
'filters'?: FilterListsResponse2;
})
    );
  }

}
